import {
  Button,
  CloseButton,
  Flex,
  Grid,
  Heading,
  IconAddLine,
  IconEditLine,
  IconTrashLine,
  List,
  Modal,
} from "@instructure/ui";
import { IconButton } from "@instructure/ui-buttons";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { CycleTypeService } from "../../services/CyclesService";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../DialogYesNo";
import { CycleTypeEditModal } from "./CycleTypeEditModal";

export const CycleTypePanel = ({
  cycleTypePanelOpen,
  setCycleTypePanelOpen,
  setTrigger,
  setLoad,
}) => {
  const showNotification = useNotification();
  const [cycleTypeList, setCycleTypeList] = useState([]);
  const [cycleTypeToRemove, setCycleTypeToRemove] = useState(null);
  const [editedType, setEditedType] = useState(null);
  const [reloadData, setReloadData] = useState(true);
  const [loading, setLoading] = useState(false);
  const [openTypeDialog, setOpenTypeDialog] = useState(false);

  useEffect(() => {
    if (reloadData) {
      //setLoading(true);
      CycleTypeService.getCycleTypes().then((cycleTypes) => {
        setCycleTypeList(cycleTypes);
        setReloadData(false);
        setLoading(false);
      });
    }
  }, [reloadData]);

  const handleEditClick = (type) => {
    setEditedType(type);
  };

  const addCycleType = () => {
    setEditedType({ name: "", duration: "" });
  };

  const removeCycleType = () => {
    CycleTypeService.deleteCycleType(cycleTypeToRemove.id)
      .then(() => {
        setCycleTypeToRemove(null);
        setReloadData(true);
        setCycleTypeList([
          ...cycleTypeList.filter((type) => type.id !== cycleTypeToRemove.id),
        ]);
        setOpenTypeDialog(false);
        showNotification(t("notifications.cycle.deleteCycleTypeSuccess"));
      })
      .catch((e) => {
        showNotification(
          `${t("notifications.cycle.deleteCycleTypeError")} ${e}`,
          ERROR
        );
      });
  };

  // const handleAddTypesModal = () => {
  //   setCycleTypePanelOpen(true);
  // };

  const handleCloseTypesModal = () => {
    setLoad(false);
    setCycleTypePanelOpen(false);
  };

  const handleEditModalClose = (updatedType) => {
    if (!updatedType || updatedType.name === "") {
      // ignore
    } else if (
      !cycleTypeList.some((element) => element.id === updatedType.id)
    ) {
      cycleTypeList.push(updatedType);
      setCycleTypeList([...cycleTypeList]);
    } else {
      cycleTypeList.forEach((type, idx) => {
        if (type.id === updatedType.id) {
          cycleTypeList[idx] = updatedType;
        }
      });
      setCycleTypeList([...cycleTypeList]);
    }
    setEditedType(null);
    setTrigger(true);
  };

  return (
    cycleTypePanelOpen && (
      <>
        <Modal
          as="form"
          open={cycleTypePanelOpen}
          onDismiss={() => {
            setCycleTypePanelOpen(false);
          }}
          size="medium"
          label={t("cyclesAndGoals.cycleType.configure")}
          shouldCloseOnDocumentClick
        >
          <Modal.Header>
            <CloseButton
              placement="end"
              offset="small"
              onClick={handleCloseTypesModal}
              screenReaderLabel={t("global.close")}
            />
            <Heading>{t("cyclesAndGoals.cycleType.configure")}</Heading>
          </Modal.Header>
          <Modal.Body>
            <Grid hAlign="end" vAlign="middle">
              <Grid.Row>
                <Grid.Col width="auto">
                  <Button
                    color="primary"
                    onClick={addCycleType}
                    renderIcon={IconAddLine}
                    key={"cycle-add-button"}
                  >
                    {t("cyclesAndGoals.cycleType.addCycleTypeButton")}
                  </Button>
                </Grid.Col>
              </Grid.Row>
            </Grid>
            <div style={{ marginTop: 15 }}>
              {loading ? (
                <Skeleton count={3} style={{ marginBottom: 10, height: 60 }} />
              ) : (
                <>
                  <List isUnstyled>
                    {cycleTypeList &&
                      cycleTypeList.map((type, key) => {
                        return (
                          <List.Item key={key}>
                            <div
                              style={{
                                backgroundColor: "#F2F8FA",
                                border: "1px solid #333",
                                borderRadius: "10px",
                                padding: "5px 15px",
                                marginBottom: 10,
                              }}
                            >
                              <Grid
                                vAlign="middle"
                                hAlign="space-between"
                                colSpacing="small"
                              >
                                <Grid.Row>
                                  <Grid.Col width={10} hAlign="space-between">
                                    <Flex>
                                      <Flex.Item shouldGrow shouldShrink>
                                        <Flex direction="column">
                                          <Flex.Item>
                                            <span style={{ color: " #0D323F" }}>
                                              {type.name}
                                            </span>
                                          </Flex.Item>
                                          <Flex.Item>
                                            <span
                                              style={{
                                                fontSize: "0.9em",
                                                color: "#156380",
                                              }}
                                            >
                                              {t(
                                                "cyclesAndGoals.cycleType.durationEvery"
                                              )}{" "}
                                              {type.duration}
                                            </span>
                                          </Flex.Item>
                                        </Flex>
                                      </Flex.Item>
                                    </Flex>
                                  </Grid.Col>
                                  <Grid.Col width="auto">
                                    <IconButton
                                      shape="rectangle"
                                      screenReaderLabel={t(
                                        "cyclesAndGoals.cycleType.editTagScreenReaderLabel"
                                      )}
                                      margin="none small none none"
                                      onClick={() => handleEditClick(type)}
                                    >
                                      <IconEditLine />
                                    </IconButton>
                                    <IconButton
                                      shape="rectangle"
                                      screenReaderLabel={t(
                                        "cyclesAndGoals.cycleType.deleteTagScreenReaderLabel"
                                      )}
                                      onClick={() => {
                                        setCycleTypeToRemove(type);
                                        setOpenTypeDialog(true);
                                      }}
                                    >
                                      <IconTrashLine />
                                    </IconButton>
                                  </Grid.Col>
                                </Grid.Row>
                              </Grid>
                            </div>
                          </List.Item>
                        );
                      })}
                  </List>
                  <DialogYesNo
                    message={t("cyclesAndGoals.cycleType.deleteCycleType")}
                    openStatus={openTypeDialog}
                    onClickNo={() => {
                      setCycleTypeToRemove(null);
                      setOpenTypeDialog(false);
                    }}
                    onClickYes={() => {
                      setLoading(true);
                      setLoad(true);
                      removeCycleType();
                    }}
                  />
                </>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={handleCloseTypesModal} margin="0 x-small 0 0">
              {t("global.close")}
            </Button>
          </Modal.Footer>
        </Modal>
        {editedType && (
          <CycleTypeEditModal
            open={true}
            cycleType={editedType}
            cycleTypes={cycleTypeList}
            onCloseHandler={handleEditModalClose}
            setLoad={setLoad}
            reloadData={setReloadData}
          />
        )}
      </>
    )
  );
};
